import { QueryClientProvider } from "@tanstack/react-query";
import { queryClient } from "@via/api/query-client.tsx";
import Notifications from "@via/components/notifications.tsx";
import { AuthProvider } from "@via/context/auth-context.tsx";
import { GlobalProvider } from "@via/context/global-context.tsx";
import { NotificationsProvider } from "@via/context/notifications-context.tsx";
import Config from "@via/utils/config.tsx";
import { extractUrlPart } from "@via/utils/helpers.tsx";
import React, { useEffect } from "react";
import ReactDOM from "react-dom/client";
import AppRoutes from "./routes.tsx";
import { initializeSentry } from "./sentry";

import "virtual:uno.css";
import "./index.css";

import { useConfigurations } from "@via/api/configuration.tsx";
import { AbilityProvider } from "@via/context/ability-context.tsx";
import { UpgradeModalProvider } from "@via/context/upgrade-modal-context.tsx";

/** Do not defy the structure of this file unless absolutely needed!
 * Reading the layout should be semantic and understandable to all!
 * We are wrapping the SideNavigation and content pages in a flex container
 * to get that fancy slide effect using flex grow / shrink **/

// Component to initialize Sentry once configuration is loaded
const SentryInitializer: React.FC = () => {
  const { data: configurations, isLoading } = useConfigurations();

  useEffect(() => {
    const sentryDsn = configurations?.SENTRY_DSN_URL;
    const environmentUrl = extractUrlPart(Config.HOST);
    const version = configurations?.VERSION;
    if (!isLoading && environmentUrl && sentryDsn) {
      initializeSentry(sentryDsn, environmentUrl, version);
    }
  }, [configurations, isLoading]);

  return null; // This component doesn't render anything
};

ReactDOM.createRoot(document.getElementById("root") as Element).render(
  <React.StrictMode>
    <QueryClientProvider client={queryClient}>
      <GlobalProvider>
        <AuthProvider>
          <AbilityProvider>
            <NotificationsProvider>
              <UpgradeModalProvider>
                <Notifications />
                <SentryInitializer />
                <AppRoutes />
              </UpgradeModalProvider>
            </NotificationsProvider>
          </AbilityProvider>
        </AuthProvider>
      </GlobalProvider>
    </QueryClientProvider>
  </React.StrictMode>
);
